<template>
    <span
        :class="disabled ? 'module-disabled-struct' : ''"
        :uk-tooltip="
            disabled
                ? 'title:' + $t('x.module.disabled.hint', {x: $t('gallery')})
                : ''
        ">
        <span
            :class="
                disabled ? 'module-disabled uk-height-1-1' : 'uk-height-1-1'
            ">
            <License
                :license-link="settings.licenseLink"
                v-if="isDateStringPast(settings.clickAndBuyLicensedUntil)"
                :licensed-until="settings.clickAndBuyLicensedUntil"
                :description="$t(moduleName)"
                class="uk-margin-medium-bottom" />
            <div
                v-else-if="!settings.clickAndBuyLicensedUntil"
                class="uk-margin-medium-bottom">
                <InfoBox mode="primary" icon="info">
                    {{
                        $t('x.module.disabled.message', {
                            x: $t(moduleName),
                        })
                    }}
                </InfoBox>
            </div>

            <ul uk-tab="animation: uk-animation-fade">
                <li
                    v-for="tab in [
                        'lunacart',
                        'variants',
                        'analysis',
                        'settings',
                    ]"
                    :key="tab">
                    <a href="#">{{ $t(tab) }}</a>
                </li>
            </ul>
            <ul class="uk-switcher uk-margin">
                <li>
                    <LoadingStruct is-animation-disabled :is-loading="loading">
                        <InfoBox
                            v-if="limitReached"
                            icon="warning"
                            mode="danger">
                            <Translate
                                translation-key="clickandbuy.limit.reached"
                                :parameter="{
                                    x: settings.clickAndBuyInfos.limit,
                                }" />
                        </InfoBox>
                        <div v-if="limitReached" class="uk-margin-medium-top" />

                        <GenericView>
                            <template v-slot:listLength>
                                {{ list.length }}
                                {{
                                    $t(
                                        list.length === 1
                                            ? 'showcase'
                                            : 'showcases'
                                    ) +
                                    ' ' +
                                    $t('overall')
                                }}
                            </template>

                            <template v-slot:newButton>
                                <span
                                    :uk-tooltip="
                                        'title:' +
                                        (limitReached
                                            ? $t('clickandbuy.limit.reached', {
                                                  x: settings.clickAndBuyInfos
                                                      .limit,
                                              })
                                            : '')
                                    ">
                                    <Button
                                        is-round
                                        :action="actionNew"
                                        :is-disabled="limitReached"
                                        has-action
                                        mode="primary">
                                        {{ $t('x.add', {x: $t('campaign')}) }}
                                    </Button>
                                </span>
                            </template>

                            <template v-slot:filter>
                                <div v-if="getFilters().length > 0">
                                    <ul
                                        class="uk-subnav uk-subnav-pill"
                                        uk-margin>
                                        <li
                                            v-for="filter in getFilters()"
                                            :key="filter"
                                            :uk-filter-control="
                                                '[data-tags*=\'' +
                                                filter +
                                                '\']'
                                            ">
                                            <a href="#">{{ $t(filter) }}</a>
                                        </li>
                                    </ul>
                                </div>
                            </template>

                            <li
                                v-for="(item, key) in computedList"
                                :key="'clickandbuy-' + key"
                                :data-tags="getFilterTags(item).join(' ')">
                                <ShowcaseItem
                                    :showcase="item"
                                    :settings="settings"
                                    @select="actionSelect" />

                                <Modal
                                    is-round
                                    is-extended
                                    :id="modalIdUpdate + item.id"
                                    :title="
                                        $t('x.change', {
                                            x: $t('campaign'),
                                        })
                                    ">
                                    <ClickAndBuyForm
                                        :id="item.id"
                                        :settings="settings"
                                        @declared="actionGet"
                                        :modal-id="modalIdUpdate + item.id"
                                        :click-and-buy="item"
                                        :variants="variants"
                                        :disabled="disabled" />
                                </Modal>
                            </li>
                        </GenericView>

                        <div
                            v-if="!computedList || computedList.length === 0"
                            class="uk-margin-small-top" />
                        <div v-if="!computedList || computedList.length === 0">
                            {{ $t('none.x', {x: $t('clickandbuys')}) }}
                        </div>
                    </LoadingStruct>
                </li>
                <li>
                    <div>
                        <ClickAndBuyVariantsForm
                            :disabled="disabled"
                            @declared="variantsDeclared"
                            :variants="settings.clickAndBuySettings.variants" />
                    </div>
                </li>
                <li>
                    <LunaStatsAdmin
                        :disabled="disabled"
                        :click-and-buy-settings="
                            settings.clickAndBuySettings
                        " />
                </li>
                <li>
                    <div
                        class="uk-child-width-1-2@m uk-child-width-1-1 uk-grid-match"
                        uk-grid>
                        <div>
                            <ClickAndBuyNameForm
                                :disabled="disabled"
                                :text="settings.clickAndBuySettings.name" />
                        </div>
                        <div>
                            <ClickAndBuyTitleForm
                                :disabled="disabled"
                                :text="settings.clickAndBuySettings.title" />
                        </div>
                        <div>
                            <ClickAndBuyCompanyForm
                                :disabled="disabled"
                                :text="settings.clickAndBuySettings.company" />
                        </div>
                        <div>
                            <ClickAndBuyLogoForm
                                :disabled="disabled"
                                :click-and-buy-only="computedClickAndBuyOnly"
                                :data="getInfos.clickAndBuy.logo"
                                @updated="receiveInfos"
                                :allowed-extensions="fileExtensionsBackground"
                                :allowed-size="allowedFaviconSize" />
                        </div>
                        <div>
                            <ClickAndBuyColorForm
                                :disabled="disabled"
                                :mode="
                                    settings.clickAndBuySettings.themeColorMode
                                "
                                :text="
                                    settings.clickAndBuySettings
                                        .themeBackgroundColor
                                " />
                        </div>
                        <div>
                            <ClickAndBuyLinksForm
                                :disabled="disabled"
                                :text="settings.clickAndBuySettings.links" />
                        </div>
                        <div>
                            <ClickAndBuyFundraisingNumberForm
                                :disabled="
                                    settings.modules &&
                                    !settings.modules.includes(
                                        'lunacartCampaign'
                                    )
                                "
                                :value="
                                    settings.clickAndBuySettings
                                        .fundraisingNumber
                                " />
                        </div>
                    </div>
                </li>
                <li v-if="false">
                    <div
                        class="uk-child-width-1-2@m uk-child-width-1-1 uk-grid-match"
                        uk-grid>
                        <div
                            v-if="!settings.clickAndBuyLicensedUntil"
                            class="uk-width-1-1">
                            <InfoBox mode="primary" icon="info">
                                {{
                                    $t('x.module.disabled.message', {
                                        x: $t(moduleName),
                                    })
                                }}
                            </InfoBox>
                        </div>
                        <div v-else class="uk-width-1-1">
                            <License
                                :license-link="settings.licenseLink"
                                :licensed-until="
                                    settings.clickAndBuyLicensedUntil
                                "
                                :description="$t(moduleName)" />
                        </div>
                    </div>
                </li>
            </ul>

            <Modal
                is-round
                is-extended
                :id="modalIdCreate"
                :title="$t('x.add', {x: $t('campaign')})">
                <ClickAndBuyForm
                    :variants="variants"
                    v-if="!loading"
                    id="new"
                    :settings="settings"
                    @declared="actionGet"
                    :modal-id="modalIdCreate"
                    :click-and-buy="{
                        id: null,

                        title: '',
                        text: '',

                        emailText: '',
                        mediaFileId: null,

                        articleFileId: null,
                        price: 0,

                        disabled: false,
                    }"
                    :disabled="disabled" />
            </Modal>
        </span>
    </span>
</template>

<script>
import License from '@/components/License.vue';
import InfoBox from '@/components/generic/InfoBox.vue';
import ClickAndBuyTitleForm from '@/views/clickandbuy/ClickAndBuyTitleForm.vue';
import ClickAndBuyColorForm from '@/views/clickandbuy/ClickAndBuyColorForm.vue';
import Translate from '@/components/Translate.vue';
import Button from '@/components/generic/Button.vue';
import UIkit from 'uikit';
import Modal from '@/components/generic/Modal.vue';
import ClickAndBuyForm from '@/views/clickandbuy/ClickAndBuyForm.vue';
import axios from 'axios';
import QRCode from 'easyqrcodejs';
import Vue from 'vue';
import ClickAndBuyLogoForm from '@/views/clickandbuy/ClickAndBuyLogoForm.vue';
import ClickAndBuyNameForm from '@/views/clickandbuy/ClickAndBuyNameForm.vue';
import ClickAndBuyLinksForm from '@/views/clickandbuy/ClickAndBuyLinksForm.vue';
import ClickAndBuyCompanyForm from '@/views/clickandbuy/ClickAndBuyCompanyForm.vue';
import ClickAndBuyVariantsForm from './ClickAndBuyVariantsForm.vue';
import GenericView from '@/components/generic/GenericView.vue';
import ShowcaseItem from '@/views/clickandbuy/ShowcaseItem.vue';
import LoadingStruct from '@/views/LoadingStruct.vue';
import LunaStatsAdmin from '@/views/clickandbuy/LunaStatsAdmin.vue';
import ClickAndBuyFundraisingNumberForm from '@/views/clickandbuy/ClickAndBuyFundraisingNumberForm.vue';

export default {
    name: 'ClickAndBuyAdmin',
    components: {
        ClickAndBuyFundraisingNumberForm,
        LunaStatsAdmin,
        LoadingStruct,
        ShowcaseItem,
        GenericView,
        ClickAndBuyVariantsForm,
        ClickAndBuyCompanyForm,
        ClickAndBuyLinksForm,
        ClickAndBuyNameForm,
        ClickAndBuyLogoForm,
        ClickAndBuyForm,
        Modal,
        Button,
        Translate,
        ClickAndBuyColorForm,
        ClickAndBuyTitleForm,
        InfoBox,
        License,
    },
    data() {
        return {
            moduleName: 'clickandbuy',

            modalIdCreate: 'modal-clickandbuy-create',
            modalIdUpdate: 'modal-clickandbuy-update-',

            selected: {},

            list: [],

            hoveredId: 0,

            qrCodes: [],
            loading: true,

            variants: [],
        };
    },
    props: {
        settings: Object,
        disabled: Boolean,
    },
    created() {
        this.actionGetVariants();
        this.actionGet();
    },
    mounted() {
        this.generateQrCodes();
    },
    methods: {
        getFilterTags(item) {
            let tags = [];

            if (!item.disabled) {
                tags.push('active');
            } else {
                tags.push('disabled');
            }

            if (!item.hidden) {
                tags.push('listed');
            } else {
                tags.push('notListed');
            }

            if (item.fundraising) {
                tags.push('fundraising');
            }

            if (item.price && item.price > 0) {
                tags.push('charged');
            } else {
                tags.push('free');
            }

            return tags;
        },
        getFilters() {
            const order = [
                'active',
                'disabled',
                'listed',
                'notListed',
                'fundraising',
                'charged',
                'free',
            ];
            let filter = [];

            for (let i = 0, j = this.list.length; i < j; i++) {
                filter.push(...this.getFilterTags(this.list[i]));
            }

            return [...new Set(filter)].sort(
                (a, b) => order.indexOf(a) - order.indexOf(b)
            );
        },
        variantsDeclared() {
            this.actionGetVariants();
            this.actionGet();
        },
        actionGetVariants() {
            let http = axios.create();
            http.get(this.apiUrl + '/manage-variants')
                .then((response) => {
                    this.variants = response.data;
                })
                .catch((e) => {
                    this.checkGenericError(e);
                });
        },
        actionGetLink(clickandbuy) {
            if (this.settings.clickAndBuyInfos.domain) {
                return (
                    'https://' +
                    this.settings.clickAndBuyInfos.domain +
                    '/' +
                    this.$i18n.locale +
                    '/' +
                    clickandbuy.id
                );
            }

            return (
                window.location.origin +
                '/' +
                this.$i18n.locale +
                '/lunacart/' +
                clickandbuy.id
            );
        },
        generateQrCodes() {
            let self = this;
            setInterval(function () {
                for (let i = 0, j = self.computedList.length; i < j; i++) {
                    let clickandbuy = self.computedList[i];

                    if (self.computedQrCodes[clickandbuy.id]) {
                        return;
                    }

                    let refElement = document.getElementById(
                        'clickandbuyqrcode' + clickandbuy.id
                    );
                    if (!refElement) {
                        return;
                    }

                    let optionsDownload = {
                        text: self.actionGetLink(clickandbuy),
                        drawer: 'svg',
                    };

                    Vue.set(
                        self.qrCodes,
                        clickandbuy.id,
                        new QRCode(refElement, optionsDownload)
                    );
                }
            }, 50);
        },
        actionNew() {
            this.selected = {};
            UIkit.modal('#' + this.modalIdCreate).show();
        },
        actionSelect(select) {
            this.selected = select;
            UIkit.modal('#' + this.modalIdUpdate + select.id).show();
        },
        actionGet(id) {
            if (id) {
                this.hoveredId = id;
            }

            let http = axios.create();
            http.get(this.apiUrl + '/manage-clickandbuys')
                .then((response) => {
                    if (response.data) {
                        this.list = response.data;
                    } else {
                        this.list = [];
                    }

                    this.qrCodes = [];

                    this.loading = false;
                })
                .catch((e) => {
                    this.checkGenericError(e);

                    this.error = e;
                });
        },
    },
    computed: {
        allowedFaviconSize() {
            if (this.settings.maxFileSizeForBackgroundPicturesInMB) {
                return this.settings.maxFileSizeForBackgroundPicturesInMB;
            }

            return 0;
        },
        fileExtensionsBackground() {
            if (this.settings.allowedFileExtensions) {
                return this.settings.allowedFileExtensions;
            }

            return [];
        },
        computedClickAndBuyOnly() {
            return (
                this.settings &&
                this.settings.clickAndBuyInfos &&
                this.settings.clickAndBuyInfos.only === true
            );
        },
        computedQrCodes() {
            return this.qrCodes;
        },
        computedList() {
            return this.list;
        },
        limitReached() {
            return (
                this.list &&
                this.settings.clickAndBuyInfos &&
                this.list.length >= this.settings.clickAndBuyInfos.limit
            );
        },
    },
};
</script>

<style lang="scss" scoped>
.thumbnail-height {
    height: 45px;
    border-radius: $border-radius-next;
}

.qr-code {
    height: 80px;
    width: 80px;
}

.lunacart-struct {
    font-size: 14px;
}
</style>
